import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { 
  fetchFiles, 
  fetchFolders,
  uploadFile, 
  deleteFile as deleteFileApi, 
  createFolder,
  deleteFolder as deleteFolderApi
} from '../services/api';

const FileContext = createContext();

export const useFiles = () => {
  const context = useContext(FileContext);
  if (!context) {
    throw new Error('useFiles deve ser usado dentro de um FileProvider');
  }
  return context;
};

export const FileProvider = ({ children }) => {
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [rootFolderId, setRootFolderId] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  
  const currentPageRef = useRef(1);

  const resetPagination = useCallback(() => {
    currentPageRef.current = 1;
    setFiles([]);
    setHasMore(true);
  }, []);

  const loadFiles = useCallback(async (isLoadMore = false) => {
    try {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        resetPagination();
      }

      const response = await fetchFiles(currentFolder, currentPageRef.current);
      const filesData = response?.items || [];
      
      if (isLoadMore) {
        setFiles(prevFiles => [...prevFiles, ...filesData]);
        currentPageRef.current += 1;
      } else {
        setFiles(filesData);
      }

      setHasMore(filesData.length === 20);
      setError(null);
    } catch (err) {
      setError('Erro ao carregar arquivos');
      toast.error('Erro ao carregar arquivos');
      if (!isLoadMore) {
        setFiles([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [currentFolder, resetPagination]);

  const loadFolders = useCallback(async () => {
    try {
      const foldersData = await fetchFolders();
      const foldersArray = Array.isArray(foldersData) ? foldersData : [];
      setFolders(foldersArray);
      
      const rootFolder = foldersArray.find(folder => folder.is_main);
      if (rootFolder) {
        setRootFolderId(rootFolder.id);
        if (currentFolder === null) {
          setCurrentFolder(rootFolder.id);
        }
      }
    } catch (err) {
      toast.error('Erro ao carregar pastas');
      setFolders([]);
    }
  }, [currentFolder]);

  useEffect(() => {
    loadFiles();
    loadFolders();
  }, [loadFiles, loadFolders]);

  const handleUpload = async (files) => {
    try {
      await uploadFile(files, currentFolder);
      toast.success('Arquivo(s) enviado(s) com sucesso!');
      loadFiles();
    } catch (error) {
      toast.error('Erro ao enviar arquivo(s)');
    }
  };

  const deleteFile = async (fileId) => {
    try {
      await deleteFileApi(fileId);
      toast.success('Arquivo excluído com sucesso!');
      loadFiles();
    } catch (error) {
      toast.error('Erro ao excluir arquivo');
    }
  };

  const createNewFolder = async (name) => {
    try {
      await createFolder(name, currentFolder);
      toast.success('Pasta criada com sucesso!');
      loadFolders();
    } catch (error) {
      toast.error('Erro ao criar pasta');
    }
  };

  const deleteFolder = async (folderId) => {
    try {
      await deleteFolderApi(folderId);
      toast.success('Pasta excluída com sucesso!');
      loadFolders();
      if (currentFolder === folderId) {
        setCurrentFolder(rootFolderId);
      }
    } catch (error) {
      toast.error('Erro ao excluir pasta');
    }
  };

  const navigateToFolder = (folderId) => {
    setCurrentFolder(folderId || rootFolderId);
    resetPagination();
  };

  const loadMoreFiles = useCallback(() => {
    if (!loadingMore && hasMore) {
      loadFiles(true);
    }
  }, [loadingMore, hasMore, loadFiles]);

  const value = {
    files,
    folders,
    loading,
    loadingMore,
    error,
    currentFolder,
    handleUpload,
    deleteFile,
    createFolder: createNewFolder,
    deleteFolder,
    navigateToFolder,
    refreshFiles: loadFiles,
    refreshFolders: loadFolders,
    loadMoreFiles,
    hasMore
  };

  return (
    <FileContext.Provider value={value}>
      {children}
    </FileContext.Provider>
  );
}; 